import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { Button } from '@/components/Button'
import { EmptyState } from '@/components/EmptyState'
import { FormEvent } from '@/components/FormEvent'
import { FormEventPerformance } from '@/components/FormEventPerformance'
import { FormEventPole } from '@/components/FormEventPole'
import { FormEventScheduleItem } from '@/components/FormEventScheduleItem'
import { useModal } from '@/hooks/useModal'
import { useToast } from '@/hooks/useToast'
import {
  cancelPolePerformance,
  cancelPoleScheduleItem,
  deleteEvent,
  deleteEventPole,
  deletePolePerformance,
  deletePoleScheduleItem,
  extractApiErrorMessage,
  fetchEventDetail,
  fetchEvents,
  publishEvent,
  publishEventPole,
  publishPolePerformance,
  publishPoleScheduleItem,
  unpublishEvent,
  unpublishEventPole,
  unpublishPolePerformance,
  unpublishPoleScheduleItem,
} from '@/services/events'
import {
  EventDetailProps,
  EventDiscoveryStatus,
  EventPerformanceProps,
  EventPoleProps,
  EventScheduleEntryProps,
  EventScheduleItemProps,
  EventSummaryProps,
  EventTheme,
  PerformanceStatus,
  ScheduleItemStatus,
  ScheduleItemType,
} from '@/types/eventsProps'
import { IoTrash } from 'react-icons/io5'
import colors from 'tailwindcss/colors'

type EventsTab = 'poles' | 'performances' | 'schedule-items' | 'schedule'

interface EventsFiltersState {
  title: string
  state: string
  year: string
  theme: EventTheme | ''
  discoveryStatus: EventDiscoveryStatus | ''
}

const DEFAULT_FILTERS: EventsFiltersState = {
  title: '',
  state: '',
  year: '',
  theme: '',
  discoveryStatus: '',
}

const eventThemeOptions: Array<{ label: string; value: EventTheme }> = [
  { label: 'Sao Joao', value: 'SAO_JOAO' },
  { label: 'Carnaval', value: 'CARNAVAL' },
  { label: 'Reveillon', value: 'REVEILLON' },
  { label: 'Copa do Mundo', value: 'COPA_DO_MUNDO' },
  { label: 'Vaquejada', value: 'VAQUEJADA' },
  { label: 'Festival', value: 'FESTIVAL' },
  { label: 'Other', value: 'OTHER' },
]

const discoveryStatusOptions: Array<{
  label: string
  value: EventDiscoveryStatus
}> = [
  { label: 'Draft', value: 'DRAFT' },
  { label: 'Published', value: 'PUBLISHED' },
  { label: 'Archived', value: 'ARCHIVED' },
  { label: 'Hidden', value: 'HIDDEN' },
]

const scheduleItemTypeLabels: Record<ScheduleItemType, string> = {
  GAME_SCREENING: 'Transmissao de jogo',
  CEREMONY: 'Cerimonia',
  OPENING: 'Abertura',
  CULTURAL_ACTIVITY: 'Atividade cultural',
  ACTIVATION: 'Ativacao',
  DJ: 'DJ',
  INFO: 'Informacao',
  OTHER: 'Outro',
}

function formatDateTime(value?: string | null) {
  if (!value) return '-'

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '-'

  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(date)
}

function resolveDiscoveryStatusClass(status: EventDiscoveryStatus) {
  switch (status) {
    case 'PUBLISHED':
      return 'border-green-200 bg-green-50 text-green-700'
    case 'ARCHIVED':
      return 'border-gray-300 bg-gray-100 text-gray-700'
    case 'HIDDEN':
      return 'border-slate-300 bg-slate-100 text-slate-700'
    default:
      return 'border-amber-200 bg-amber-50 text-amber-700'
  }
}

function resolvePublicationStatusClass(
  status: PerformanceStatus | ScheduleItemStatus,
) {
  switch (status) {
    case 'PUBLISHED':
      return 'border-green-200 bg-green-50 text-green-700'
    case 'CANCELLED':
      return 'border-red-200 bg-red-50 text-red-700'
    default:
      return 'border-amber-200 bg-amber-50 text-amber-700'
  }
}

function sortPoles(poles: EventPoleProps[]) {
  return [...poles].sort((first, second) => {
    const firstOrder = first.sortOrder ?? Number.MAX_SAFE_INTEGER
    const secondOrder = second.sortOrder ?? Number.MAX_SAFE_INTEGER

    if (firstOrder !== secondOrder) {
      return firstOrder - secondOrder
    }

    return first.name.localeCompare(second.name)
  })
}

function sortPerformances(performances: EventPerformanceProps[]) {
  return [...performances].sort((first, second) => {
    const firstDate = first.performanceDate
      ? new Date(first.performanceDate).getTime()
      : 0
    const secondDate = second.performanceDate
      ? new Date(second.performanceDate).getTime()
      : 0

    if (firstDate !== secondDate) {
      return firstDate - secondDate
    }

    const firstOrder = first.sortOrder ?? Number.MAX_SAFE_INTEGER
    const secondOrder = second.sortOrder ?? Number.MAX_SAFE_INTEGER

    if (firstOrder !== secondOrder) {
      return firstOrder - secondOrder
    }

    return (first.title ?? first.artist?.title ?? '').localeCompare(
      second.title ?? second.artist?.title ?? '',
    )
  })
}

function sortScheduleItems(items: EventScheduleItemProps[]) {
  return [...items].sort((first, second) => {
    const firstDate = first.startsAt ? new Date(first.startsAt).getTime() : 0
    const secondDate = second.startsAt ? new Date(second.startsAt).getTime() : 0

    if (firstDate !== secondDate) {
      return firstDate - secondDate
    }

    const firstOrder = first.sortOrder ?? Number.MAX_SAFE_INTEGER
    const secondOrder = second.sortOrder ?? Number.MAX_SAFE_INTEGER

    if (firstOrder !== secondOrder) {
      return firstOrder - secondOrder
    }

    return first.title.localeCompare(second.title)
  })
}

function sortScheduleEntries(entries: EventScheduleEntryProps[]) {
  return [...entries].sort((first, second) => {
    const firstOrder = first.sortOrder ?? Number.MAX_SAFE_INTEGER
    const secondOrder = second.sortOrder ?? Number.MAX_SAFE_INTEGER

    if (firstOrder !== secondOrder) {
      return firstOrder - secondOrder
    }

    const firstDate = first.startsAt ? new Date(first.startsAt).getTime() : 0
    const secondDate = second.startsAt ? new Date(second.startsAt).getTime() : 0

    if (firstDate !== secondDate) {
      return firstDate - secondDate
    }

    return (first.title ?? first.artistName ?? '').localeCompare(
      second.title ?? second.artistName ?? '',
    )
  })
}

function hasPoleLocation(pole: EventPoleProps) {
  return Boolean(pole.placeName?.trim() || pole.addressLine?.trim())
}

function FilterToggle({
  checked,
  onChange,
  label,
}: {
  checked: boolean
  onChange: (checked: boolean) => void
  label: string
}) {
  return (
    <label className="inline-flex cursor-pointer items-center gap-2 text-sm font-semibold text-gray-600">
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.currentTarget.checked)}
      />
      {label}
    </label>
  )
}

export function Events() {
  const { showToast } = useToast()
  const { openModal } = useModal()

  const [filters, setFilters] = useState<EventsFiltersState>(DEFAULT_FILTERS)
  const [appliedFilters, setAppliedFilters] =
    useState<EventsFiltersState>(DEFAULT_FILTERS)
  const appliedFiltersRef = useRef<EventsFiltersState>(DEFAULT_FILTERS)

  const [events, setEvents] = useState<EventSummaryProps[]>([])
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [lastPage, setLastPage] = useState(1)
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null)
  const [eventDetail, setEventDetail] = useState<EventDetailProps | null>(null)
  const [selectedPoleId, setSelectedPoleId] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<EventsTab>('poles')
  const [showCancelledPerformances, setShowCancelledPerformances] =
    useState(false)
  const [showCancelledScheduleItems, setShowCancelledScheduleItems] =
    useState(false)
  const [showCancelledSchedule, setShowCancelledSchedule] = useState(false)
  const [isLoadingList, setIsLoadingList] = useState(false)
  const [isLoadingDetail, setIsLoadingDetail] = useState(false)
  const [runningAction, setRunningAction] = useState<string | null>(null)

  const selectedPole = useMemo(
    () => eventDetail?.poles.find((pole) => pole.id === selectedPoleId) ?? null,
    [eventDetail?.poles, selectedPoleId],
  )

  const hasPublishedPole = useMemo(
    () =>
      Boolean(
        eventDetail?.poles.some((pole) => pole.discoveryStatus === 'PUBLISHED'),
      ),
    [eventDetail?.poles],
  )

  const totalPerformancesCount = useMemo(
    () =>
      eventDetail?.poles.reduce(
        (count, pole) => count + (pole.performances?.length ?? 0),
        0,
      ) ?? 0,
    [eventDetail?.poles],
  )

  const totalScheduleItemsCount = useMemo(
    () =>
      eventDetail?.poles.reduce(
        (count, pole) => count + (pole.scheduleItems?.length ?? 0),
        0,
      ) ?? 0,
    [eventDetail?.poles],
  )

  const selectedPolePerformances = useMemo(() => {
    const entries = sortPerformances(selectedPole?.performances ?? [])

    if (showCancelledPerformances) {
      return entries
    }

    return entries.filter((entry) => entry.status !== 'CANCELLED')
  }, [selectedPole?.performances, showCancelledPerformances])

  const selectedPoleScheduleItems = useMemo(() => {
    const entries = sortScheduleItems(selectedPole?.scheduleItems ?? [])

    if (showCancelledScheduleItems) {
      return entries
    }

    return entries.filter((entry) => entry.status !== 'CANCELLED')
  }, [selectedPole?.scheduleItems, showCancelledScheduleItems])

  const selectedPoleSchedule = useMemo(() => {
    const entries = sortScheduleEntries(selectedPole?.schedule ?? [])

    if (showCancelledSchedule) {
      return entries
    }

    return entries.filter((entry) => entry.status !== 'CANCELLED')
  }, [selectedPole?.schedule, showCancelledSchedule])

  const loadEvents = useCallback(
    async (nextPage = 1, filtersOverride?: EventsFiltersState) => {
      try {
        setIsLoadingList(true)

        const activeFilters = filtersOverride ?? appliedFiltersRef.current
        const year = Number(activeFilters.year)

        const response = await fetchEvents({
          page: nextPage,
          limit: 20,
          title: activeFilters.title,
          state: activeFilters.state,
          year: Number.isFinite(year) && year > 0 ? year : undefined,
          theme: activeFilters.theme,
          discoveryStatus: activeFilters.discoveryStatus,
        })

        const fetchedEvents = response.data
        setEvents(fetchedEvents)
        setTotal(response.meta.total)
        setPage(response.meta.page)
        setLastPage(response.meta.lastPage)

        if (fetchedEvents.length === 0) {
          setSelectedEventId(null)
          setEventDetail(null)
          setSelectedPoleId(null)
          return
        }

        setSelectedEventId((currentSelectedEventId) => {
          const eventStillVisible = currentSelectedEventId
            ? fetchedEvents.some((event) => event.id === currentSelectedEventId)
            : false

          return eventStillVisible
            ? currentSelectedEventId
            : fetchedEvents[0]?.id ?? null
        })
      } catch (error) {
        showToast(
          extractApiErrorMessage(
            error,
            'Nao foi possivel carregar os eventos.',
          ),
          {
            type: 'error',
            theme: 'light',
          },
        )
      } finally {
        setIsLoadingList(false)
      }
    },
    [showToast],
  )

  const loadEventDetail = useCallback(
    async (eventId: string) => {
      try {
        setIsLoadingDetail(true)
        const response = await fetchEventDetail(eventId)
        const sortedPoles = sortPoles(response.poles)

        setEventDetail({
          ...response,
          poles: sortedPoles,
        })

        if (sortedPoles.length === 0) {
          setSelectedPoleId(null)
          return
        }

        setSelectedPoleId((currentSelectedPoleId) => {
          if (
            currentSelectedPoleId &&
            sortedPoles.some((pole) => pole.id === currentSelectedPoleId)
          ) {
            return currentSelectedPoleId
          }

          return sortedPoles[0].id
        })
      } catch (error) {
        showToast(
          extractApiErrorMessage(
            error,
            'Nao foi possivel carregar o detalhe do evento.',
          ),
          {
            type: 'error',
            theme: 'light',
          },
        )
      } finally {
        setIsLoadingDetail(false)
      }
    },
    [showToast],
  )

  const refreshCurrentContext = useCallback(async () => {
    await loadEvents(page)

    if (selectedEventId) {
      await loadEventDetail(selectedEventId)
    }
  }, [loadEventDetail, loadEvents, page, selectedEventId])

  const runProtectedAction = useCallback(
    async (actionKey: string, action: () => Promise<void>) => {
      try {
        setRunningAction(actionKey)
        await action()
      } finally {
        setRunningAction(null)
      }
    },
    [],
  )

  const handlePublishEvent = useCallback(async () => {
    if (!selectedEventId) return

    if (!hasPublishedPole) {
      showToast('Publique ao menos um polo antes de publicar o evento.', {
        type: 'warning',
        theme: 'light',
      })
      return
    }

    await runProtectedAction('publish-event', async () => {
      try {
        await publishEvent(selectedEventId)
        showToast('Evento publicado com sucesso.', {
          type: 'success',
          theme: 'light',
        })
        await refreshCurrentContext()
      } catch (error) {
        showToast(
          extractApiErrorMessage(error, 'Nao foi possivel publicar o evento.'),
          {
            type: 'error',
            theme: 'light',
          },
        )
      }
    })
  }, [
    hasPublishedPole,
    refreshCurrentContext,
    runProtectedAction,
    selectedEventId,
    showToast,
  ])

  const handleUnpublishEvent = useCallback(async () => {
    if (!selectedEventId) return

    await runProtectedAction('unpublish-event', async () => {
      try {
        await unpublishEvent(selectedEventId)
        showToast('Evento despublicado com sucesso.', {
          type: 'success',
          theme: 'light',
        })
        await refreshCurrentContext()
      } catch (error) {
        showToast(
          extractApiErrorMessage(
            error,
            'Nao foi possivel despublicar o evento.',
          ),
          {
            type: 'error',
            theme: 'light',
          },
        )
      }
    })
  }, [refreshCurrentContext, runProtectedAction, selectedEventId, showToast])

  const handleDeleteEvent = useCallback(
    (eventId: string) => {
      openModal({
        title: 'Atencao',
        description: 'Deseja remover este evento? A remocao e soft delete.',
        textConfirm: 'Excluir',
        confirm() {
          runProtectedAction(`delete-event-${eventId}`, async () => {
            try {
              await deleteEvent(eventId)
              showToast('Evento removido com sucesso.', {
                type: 'success',
                theme: 'light',
              })
              await loadEvents(1)
            } catch (error) {
              showToast(
                extractApiErrorMessage(
                  error,
                  'Nao foi possivel remover o evento.',
                ),
                {
                  type: 'error',
                  theme: 'light',
                },
              )
            }
          })
        },
      })
    },
    [loadEvents, openModal, runProtectedAction, showToast],
  )

  const handlePublishPole = useCallback(
    async (pole: EventPoleProps) => {
      if (!selectedEventId) return

      if (!hasPoleLocation(pole)) {
        showToast(
          'Informe o nome do local ou o endereco antes de publicar o polo.',
          {
            type: 'warning',
            theme: 'light',
          },
        )
        return
      }

      await runProtectedAction(`publish-pole-${pole.id}`, async () => {
        try {
          await publishEventPole(selectedEventId, pole.id)
          showToast('Polo publicado com sucesso.', {
            type: 'success',
            theme: 'light',
          })
          await refreshCurrentContext()
        } catch (error) {
          showToast(
            extractApiErrorMessage(error, 'Nao foi possivel publicar o polo.'),
            {
              type: 'error',
              theme: 'light',
            },
          )
        }
      })
    },
    [refreshCurrentContext, runProtectedAction, selectedEventId, showToast],
  )

  const handleUnpublishPole = useCallback(
    async (pole: EventPoleProps) => {
      if (!selectedEventId) return

      await runProtectedAction(`unpublish-pole-${pole.id}`, async () => {
        try {
          await unpublishEventPole(selectedEventId, pole.id)
          showToast('Polo despublicado com sucesso.', {
            type: 'success',
            theme: 'light',
          })
          await refreshCurrentContext()
        } catch (error) {
          showToast(
            extractApiErrorMessage(
              error,
              'Nao foi possivel despublicar o polo.',
            ),
            {
              type: 'error',
              theme: 'light',
            },
          )
        }
      })
    },
    [refreshCurrentContext, runProtectedAction, selectedEventId, showToast],
  )

  const handleDeletePole = useCallback(
    (pole: EventPoleProps) => {
      if (!selectedEventId) return

      openModal({
        title: 'Atencao',
        description: 'Deseja remover este polo? A remocao e soft delete.',
        textConfirm: 'Excluir',
        confirm() {
          runProtectedAction(`delete-pole-${pole.id}`, async () => {
            try {
              await deleteEventPole(selectedEventId, pole.id)
              showToast('Polo removido com sucesso.', {
                type: 'success',
                theme: 'light',
              })
              await refreshCurrentContext()
            } catch (error) {
              showToast(
                extractApiErrorMessage(
                  error,
                  'Nao foi possivel remover o polo.',
                ),
                {
                  type: 'error',
                  theme: 'light',
                },
              )
            }
          })
        },
      })
    },
    [
      openModal,
      refreshCurrentContext,
      runProtectedAction,
      selectedEventId,
      showToast,
    ],
  )

  const handlePublishPerformance = useCallback(
    async (performance: EventPerformanceProps) => {
      if (!selectedEventId || !selectedPoleId || !selectedPole) return

      if (selectedPole.discoveryStatus !== 'PUBLISHED') {
        showToast('Publique o polo antes de publicar a performance.', {
          type: 'warning',
          theme: 'light',
        })
        return
      }

      await runProtectedAction(
        `publish-performance-${performance.id}`,
        async () => {
          try {
            await publishPolePerformance(
              selectedEventId,
              selectedPoleId,
              performance.id,
            )
            showToast('Performance publicada com sucesso.', {
              type: 'success',
              theme: 'light',
            })
            await refreshCurrentContext()
          } catch (error) {
            showToast(
              extractApiErrorMessage(
                error,
                'Nao foi possivel publicar a performance.',
              ),
              {
                type: 'error',
                theme: 'light',
              },
            )
          }
        },
      )
    },
    [
      refreshCurrentContext,
      runProtectedAction,
      selectedEventId,
      selectedPole,
      selectedPoleId,
      showToast,
    ],
  )

  const handleUnpublishPerformance = useCallback(
    async (performance: EventPerformanceProps) => {
      if (!selectedEventId || !selectedPoleId) return

      await runProtectedAction(
        `unpublish-performance-${performance.id}`,
        async () => {
          try {
            await unpublishPolePerformance(
              selectedEventId,
              selectedPoleId,
              performance.id,
            )
            showToast('Performance despublicada com sucesso.', {
              type: 'success',
              theme: 'light',
            })
            await refreshCurrentContext()
          } catch (error) {
            showToast(
              extractApiErrorMessage(
                error,
                'Nao foi possivel despublicar a performance.',
              ),
              {
                type: 'error',
                theme: 'light',
              },
            )
          }
        },
      )
    },
    [
      refreshCurrentContext,
      runProtectedAction,
      selectedEventId,
      selectedPoleId,
      showToast,
    ],
  )

  const handleCancelPerformance = useCallback(
    async (performance: EventPerformanceProps) => {
      if (!selectedEventId || !selectedPoleId) return

      await runProtectedAction(
        `cancel-performance-${performance.id}`,
        async () => {
          try {
            await cancelPolePerformance(
              selectedEventId,
              selectedPoleId,
              performance.id,
            )
            showToast('Performance cancelada com sucesso.', {
              type: 'success',
              theme: 'light',
            })
            await refreshCurrentContext()
          } catch (error) {
            showToast(
              extractApiErrorMessage(
                error,
                'Nao foi possivel cancelar a performance.',
              ),
              {
                type: 'error',
                theme: 'light',
              },
            )
          }
        },
      )
    },
    [
      refreshCurrentContext,
      runProtectedAction,
      selectedEventId,
      selectedPoleId,
      showToast,
    ],
  )

  const handleDeletePerformance = useCallback(
    (performance: EventPerformanceProps) => {
      if (!selectedEventId || !selectedPoleId) return

      openModal({
        title: 'Atencao',
        description:
          'Deseja remover esta performance? A remocao e soft delete.',
        textConfirm: 'Excluir',
        confirm() {
          runProtectedAction(
            `delete-performance-${performance.id}`,
            async () => {
              try {
                await deletePolePerformance(
                  selectedEventId,
                  selectedPoleId,
                  performance.id,
                )
                showToast('Performance removida com sucesso.', {
                  type: 'success',
                  theme: 'light',
                })
                await refreshCurrentContext()
              } catch (error) {
                showToast(
                  extractApiErrorMessage(
                    error,
                    'Nao foi possivel remover a performance.',
                  ),
                  {
                    type: 'error',
                    theme: 'light',
                  },
                )
              }
            },
          )
        },
      })
    },
    [
      openModal,
      refreshCurrentContext,
      runProtectedAction,
      selectedEventId,
      selectedPoleId,
      showToast,
    ],
  )

  const handlePublishScheduleItem = useCallback(
    async (scheduleItem: EventScheduleItemProps) => {
      if (!selectedEventId || !selectedPoleId) return

      await runProtectedAction(
        `publish-schedule-item-${scheduleItem.id}`,
        async () => {
          try {
            await publishPoleScheduleItem(
              selectedEventId,
              selectedPoleId,
              scheduleItem.id,
            )
            showToast('Schedule item publicado com sucesso.', {
              type: 'success',
              theme: 'light',
            })
            await refreshCurrentContext()
          } catch (error) {
            showToast(
              extractApiErrorMessage(
                error,
                'Nao foi possivel publicar o schedule item.',
              ),
              {
                type: 'error',
                theme: 'light',
              },
            )
          }
        },
      )
    },
    [
      refreshCurrentContext,
      runProtectedAction,
      selectedEventId,
      selectedPoleId,
      showToast,
    ],
  )

  const handleUnpublishScheduleItem = useCallback(
    async (scheduleItem: EventScheduleItemProps) => {
      if (!selectedEventId || !selectedPoleId) return

      await runProtectedAction(
        `unpublish-schedule-item-${scheduleItem.id}`,
        async () => {
          try {
            await unpublishPoleScheduleItem(
              selectedEventId,
              selectedPoleId,
              scheduleItem.id,
            )
            showToast('Schedule item despublicado com sucesso.', {
              type: 'success',
              theme: 'light',
            })
            await refreshCurrentContext()
          } catch (error) {
            showToast(
              extractApiErrorMessage(
                error,
                'Nao foi possivel despublicar o schedule item.',
              ),
              {
                type: 'error',
                theme: 'light',
              },
            )
          }
        },
      )
    },
    [
      refreshCurrentContext,
      runProtectedAction,
      selectedEventId,
      selectedPoleId,
      showToast,
    ],
  )

  const handleCancelScheduleItem = useCallback(
    async (scheduleItem: EventScheduleItemProps) => {
      if (!selectedEventId || !selectedPoleId) return

      await runProtectedAction(
        `cancel-schedule-item-${scheduleItem.id}`,
        async () => {
          try {
            await cancelPoleScheduleItem(
              selectedEventId,
              selectedPoleId,
              scheduleItem.id,
            )
            showToast('Schedule item cancelado com sucesso.', {
              type: 'success',
              theme: 'light',
            })
            await refreshCurrentContext()
          } catch (error) {
            showToast(
              extractApiErrorMessage(
                error,
                'Nao foi possivel cancelar o schedule item.',
              ),
              {
                type: 'error',
                theme: 'light',
              },
            )
          }
        },
      )
    },
    [
      refreshCurrentContext,
      runProtectedAction,
      selectedEventId,
      selectedPoleId,
      showToast,
    ],
  )

  const handleDeleteScheduleItem = useCallback(
    (scheduleItem: EventScheduleItemProps) => {
      if (!selectedEventId || !selectedPoleId) return

      openModal({
        title: 'Atencao',
        description:
          'Deseja remover este schedule item? A remocao e soft delete.',
        textConfirm: 'Excluir',
        confirm() {
          runProtectedAction(
            `delete-schedule-item-${scheduleItem.id}`,
            async () => {
              try {
                await deletePoleScheduleItem(
                  selectedEventId,
                  selectedPoleId,
                  scheduleItem.id,
                )
                showToast('Schedule item removido com sucesso.', {
                  type: 'success',
                  theme: 'light',
                })
                await refreshCurrentContext()
              } catch (error) {
                showToast(
                  extractApiErrorMessage(
                    error,
                    'Nao foi possivel remover o schedule item.',
                  ),
                  {
                    type: 'error',
                    theme: 'light',
                  },
                )
              }
            },
          )
        },
      })
    },
    [
      openModal,
      refreshCurrentContext,
      runProtectedAction,
      selectedEventId,
      selectedPoleId,
      showToast,
    ],
  )

  const handleSelectEvent = useCallback((eventId: string) => {
    setSelectedEventId(eventId)
    setActiveTab('poles')
  }, [])

  const handleSelectPoleTab = useCallback((poleId: string, tab: EventsTab) => {
    setSelectedPoleId(poleId)
    setActiveTab(tab)
  }, [])

  useEffect(() => {
    appliedFiltersRef.current = appliedFilters
  }, [appliedFilters])

  useEffect(() => {
    loadEvents(1, DEFAULT_FILTERS)
  }, [loadEvents])

  useEffect(() => {
    if (!selectedEventId) return
    loadEventDetail(selectedEventId)
  }, [loadEventDetail, selectedEventId])

  return (
    <div className="pt-6">
      <section className="rounded-2xl border bg-white p-6 md:p-4">
        <div className="flex flex-col gap-3">
          <div className="grid min-w-0 grid-cols-5 gap-3 lg:grid-cols-3 md:grid-cols-2">
            <input
              placeholder="Titulo"
              value={filters.title}
              onChange={(event) => {
                const nextValue = event.currentTarget.value

                setFilters((current) => ({
                  ...current,
                  title: nextValue,
                }))
              }}
              className="rounded border border-gray-300 px-3 py-2 text-sm focus:border-gray-500 focus:outline-none"
            />

            <input
              placeholder="UF"
              maxLength={2}
              value={filters.state}
              onChange={(event) => {
                const nextValue = event.currentTarget.value.toUpperCase()

                setFilters((current) => ({
                  ...current,
                  state: nextValue,
                }))
              }}
              className="rounded border border-gray-300 px-3 py-2 text-sm uppercase focus:border-gray-500 focus:outline-none"
            />

            <input
              placeholder="Ano"
              type="number"
              value={filters.year}
              onChange={(event) => {
                const nextValue = event.currentTarget.value

                setFilters((current) => ({
                  ...current,
                  year: nextValue,
                }))
              }}
              className="rounded border border-gray-300 px-3 py-2 text-sm focus:border-gray-500 focus:outline-none"
            />

            <select
              value={filters.theme}
              onChange={(event) => {
                const nextValue = event.currentTarget.value as EventTheme | ''

                setFilters((current) => ({
                  ...current,
                  theme: nextValue,
                }))
              }}
              className="rounded border border-gray-300 px-3 py-2 text-sm focus:border-gray-500 focus:outline-none"
            >
              <option value="">Todos os temas</option>
              {eventThemeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>

            <select
              value={filters.discoveryStatus}
              onChange={(event) => {
                const nextValue = event.currentTarget.value as
                  | EventDiscoveryStatus
                  | ''

                setFilters((current) => ({
                  ...current,
                  discoveryStatus: nextValue,
                }))
              }}
              className="rounded border border-gray-300 px-3 py-2 text-sm focus:border-gray-500 focus:outline-none"
            >
              <option value="">Todos os status</option>
              {discoveryStatusOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center justify-end gap-2 md:justify-start">
            <Button
              title="Filtrar"
              variant="outline"
              className="w-[120px]"
              onClick={() => {
                setAppliedFilters(filters)
                appliedFiltersRef.current = filters
                loadEvents(1, filters)
              }}
            />
            <Button
              title="Limpar"
              variant="outline"
              className="w-[120px]"
              onClick={() => {
                setFilters(DEFAULT_FILTERS)
                setAppliedFilters(DEFAULT_FILTERS)
                appliedFiltersRef.current = DEFAULT_FILTERS
                loadEvents(1, DEFAULT_FILTERS)
              }}
            />
            <Button
              title="Novo evento"
              variant="purple"
              className="w-[140px]"
              onClick={() =>
                openModal({
                  children: (
                    <FormEvent
                      onSaved={async () => {
                        await loadEvents(1)
                      }}
                    />
                  ),
                })
              }
            />
          </div>
        </div>
      </section>

      <section className="mt-6 rounded-2xl border bg-white p-6 md:p-4">
        <div className="flex items-center justify-between gap-3 md:flex-col md:items-start">
          <h3 className="text-lg font-bold text-gray-700">Eventos</h3>
          <p className="text-sm text-gray-500">
            Exibindo {events.length} de {total} eventos
          </p>
        </div>

        {isLoadingList && (
          <div className="mt-6 flex items-center gap-3 text-sm text-gray-500">
            <span className="h-4 w-4 animate-spin rounded-full border-2 border-purple-200 border-t-purple-600" />
            Carregando eventos...
          </div>
        )}

        {!isLoadingList && events.length === 0 && (
          <EmptyState
            title="Nenhum evento encontrado"
            description="Crie um novo evento para iniciar o fluxo de polos, performances e agenda."
          />
        )}

        <div className="mt-6 space-y-4">
          {events.map((event) => {
            const isSelected = selectedEventId === event.id

            return (
              <div
                key={event.id}
                className={`rounded-2xl border p-4 transition-colors ${
                  isSelected ? 'border-purple-400 bg-purple-50/50' : 'bg-white'
                }`}
              >
                <div className="flex items-start justify-between gap-4 md:flex-col">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 md:flex-wrap">
                      <span
                        className={`rounded-full border px-2 py-1 text-xs font-semibold ${resolveDiscoveryStatusClass(
                          event.discoveryStatus,
                        )}`}
                      >
                        {event.discoveryStatus}
                      </span>
                      <p className="text-xs uppercase tracking-wide text-gray-400">
                        {event.theme ?? '-'}{' '}
                        {event.editionYear ? `- ${event.editionYear}` : ''}
                      </p>
                    </div>

                    <p className="mt-2 truncate text-base font-bold text-gray-700">
                      {event.title}
                    </p>
                    <p className="mt-1 text-sm text-gray-500">
                      {event.city ?? '-'} / {event.state ?? '-'} - Publicado:{' '}
                      {formatDateTime(event.publishedAt)}
                    </p>
                  </div>

                  <div className="flex shrink-0 items-center gap-2 md:w-full">
                    <Button
                      title={isSelected ? 'Selecionado' : 'Abrir'}
                      variant={isSelected ? 'purple' : 'outline'}
                      className="w-[120px] md:flex-1"
                      onClick={() => handleSelectEvent(event.id)}
                    />
                    <Button
                      title="Editar"
                      variant="outline"
                      className="w-[100px] md:flex-1"
                      onClick={() =>
                        openModal({
                          children: (
                            <FormEvent
                              event={event}
                              onSaved={async () => {
                                await refreshCurrentContext()
                              }}
                            />
                          ),
                        })
                      }
                    />
                    <button
                      title="Excluir evento"
                      className="flex h-9 w-9 items-center justify-center rounded-lg border border-red-600 bg-red-600 transition hover:bg-red-700"
                      onClick={() => handleDeleteEvent(event.id)}
                    >
                      <IoTrash size={14} color={colors.white} />
                    </button>
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        {events.length > 0 && (
          <div className="mt-6 flex items-center justify-end gap-2 md:flex-wrap">
            <Button
              title="Anterior"
              variant="outline"
              className="w-[110px]"
              disabled={page <= 1 || isLoadingList}
              onClick={() => loadEvents(Math.max(1, page - 1))}
            />
            <p className="text-sm font-semibold text-gray-600">
              Pagina {page} de {lastPage}
            </p>
            <Button
              title="Proxima"
              variant="outline"
              className="w-[110px]"
              disabled={page >= lastPage || isLoadingList}
              onClick={() => loadEvents(Math.min(lastPage, page + 1))}
            />
          </div>
        )}
      </section>

      {selectedEventId && (
        <section className="mt-6 rounded-2xl border bg-white p-6 md:p-4">
          {isLoadingDetail && (
            <div className="flex items-center gap-3 text-sm text-gray-500">
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-purple-200 border-t-purple-600" />
              Carregando detalhe do evento...
            </div>
          )}

          {!isLoadingDetail && eventDetail && (
            <>
              <div className="flex items-start justify-between gap-4 md:flex-col">
                <div>
                  <h3 className="text-lg font-bold text-gray-700">
                    {eventDetail.event.title}
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    {eventDetail.event.city} / {eventDetail.event.state}
                  </p>
                  <p className="mt-1 text-sm text-gray-500">
                    Periodo: {formatDateTime(eventDetail.event.startDate)} ate{' '}
                    {formatDateTime(eventDetail.event.endDate)}
                  </p>
                </div>
                <div className="flex gap-2 md:w-full md:flex-col">
                  {eventDetail.event.discoveryStatus === 'PUBLISHED' ? (
                    <Button
                      title="Despublicar evento"
                      variant="outline"
                      className="w-[180px] md:w-full"
                      isLoading={runningAction === 'unpublish-event'}
                      onClick={handleUnpublishEvent}
                    />
                  ) : (
                    <Button
                      title="Publicar evento"
                      variant="purple"
                      className="w-[180px] md:w-full"
                      disabled={
                        !hasPublishedPole || runningAction === 'publish-event'
                      }
                      isLoading={runningAction === 'publish-event'}
                      onClick={handlePublishEvent}
                    />
                  )}
                </div>
              </div>

              {!hasPublishedPole &&
                eventDetail.event.discoveryStatus !== 'PUBLISHED' && (
                  <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
                    Para publicar o evento, e necessario ter pelo menos um polo
                    publicado.
                  </div>
                )}

              <div className="mt-6 flex flex-wrap gap-2">
                <button
                  className={`rounded-lg px-4 py-2 text-sm font-semibold transition ${
                    activeTab === 'poles'
                      ? 'bg-purple-600 text-white'
                      : 'border border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                  onClick={() => setActiveTab('poles')}
                >
                  Polos ({eventDetail.poles.length})
                </button>
                <button
                  className={`rounded-lg px-4 py-2 text-sm font-semibold transition ${
                    activeTab === 'performances'
                      ? 'bg-purple-600 text-white'
                      : 'border border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                  onClick={() => setActiveTab('performances')}
                >
                  Performances ({totalPerformancesCount})
                </button>
                <button
                  className={`rounded-lg px-4 py-2 text-sm font-semibold transition ${
                    activeTab === 'schedule-items'
                      ? 'bg-purple-600 text-white'
                      : 'border border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                  onClick={() => setActiveTab('schedule-items')}
                >
                  Schedule items ({totalScheduleItemsCount})
                </button>
                <button
                  className={`rounded-lg px-4 py-2 text-sm font-semibold transition ${
                    activeTab === 'schedule'
                      ? 'bg-purple-600 text-white'
                      : 'border border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                  onClick={() => setActiveTab('schedule')}
                >
                  Agenda consolidada
                </button>
              </div>

              {activeTab === 'poles' && (
                <div className="mt-6">
                  <div className="mb-4 flex items-center justify-between gap-3 md:flex-col md:items-stretch">
                    <h4 className="text-base font-bold text-gray-700">
                      Polos do evento
                    </h4>
                    <Button
                      title="Adicionar polo"
                      variant="purple"
                      className="w-[150px] md:w-full"
                      onClick={() =>
                        openModal({
                          children: (
                            <FormEventPole
                              eventId={selectedEventId}
                              onSaved={async () => {
                                await refreshCurrentContext()
                              }}
                            />
                          ),
                        })
                      }
                    />
                  </div>

                  {eventDetail.poles.length === 0 && (
                    <EmptyState
                      title="Nenhum polo cadastrado"
                      description="Crie pelo menos um polo para conseguir publicar este evento."
                    />
                  )}

                  <div className="space-y-3">
                    {eventDetail.poles.map((pole) => (
                      <div
                        key={pole.id}
                        className={`rounded-xl border p-4 ${
                          selectedPoleId === pole.id
                            ? 'border-purple-300 bg-purple-50/50'
                            : 'bg-white'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-4 md:flex-col">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 md:flex-wrap">
                              <span
                                className={`rounded-full border px-2 py-1 text-xs font-semibold ${resolveDiscoveryStatusClass(
                                  pole.discoveryStatus,
                                )}`}
                              >
                                {pole.discoveryStatus}
                              </span>
                              {pole.isMain && (
                                <span className="rounded-full border border-purple-200 bg-purple-100 px-2 py-1 text-xs font-semibold text-purple-700">
                                  Polo principal
                                </span>
                              )}
                            </div>
                            <p className="mt-2 font-bold text-gray-700">
                              {pole.name}
                            </p>
                            <p className="mt-1 text-sm text-gray-500">
                              {pole.city ?? '-'} / {pole.state ?? '-'} -{' '}
                              {pole.placeName ||
                                pole.addressLine ||
                                'Sem local informado'}
                            </p>
                            <p className="mt-1 text-sm text-gray-500">
                              Performances: {pole.performances?.length ?? 0}
                            </p>
                            <p className="mt-1 text-sm text-gray-500">
                              Schedule items: {pole.scheduleItems?.length ?? 0}
                            </p>
                            {!hasPoleLocation(pole) &&
                              pole.discoveryStatus !== 'PUBLISHED' && (
                                <p className="mt-2 text-xs font-semibold text-amber-700">
                                  Para publicar, informe local ou endereco.
                                </p>
                              )}
                          </div>
                          <div className="flex shrink-0 flex-wrap items-center gap-2 md:w-full">
                            <Button
                              title="Editar"
                              variant="outline"
                              className="w-[90px] md:flex-1"
                              onClick={() =>
                                openModal({
                                  children: (
                                    <FormEventPole
                                      eventId={selectedEventId}
                                      pole={pole}
                                      onSaved={async () => {
                                        await refreshCurrentContext()
                                      }}
                                    />
                                  ),
                                })
                              }
                            />

                            {pole.discoveryStatus === 'PUBLISHED' ? (
                              <Button
                                title="Despublicar"
                                variant="outline"
                                className="w-[120px] md:flex-1"
                                isLoading={
                                  runningAction === `unpublish-pole-${pole.id}`
                                }
                                onClick={() => handleUnpublishPole(pole)}
                              />
                            ) : (
                              <Button
                                title="Publicar"
                                variant="purple"
                                className="w-[100px] md:flex-1"
                                isLoading={
                                  runningAction === `publish-pole-${pole.id}`
                                }
                                onClick={() => handlePublishPole(pole)}
                              />
                            )}

                            <Button
                              title="Lineup"
                              variant="outline"
                              className="w-[90px] md:flex-1"
                              onClick={() =>
                                handleSelectPoleTab(pole.id, 'performances')
                              }
                            />

                            <Button
                              title="Agenda"
                              variant="outline"
                              className="w-[90px] md:flex-1"
                              onClick={() =>
                                handleSelectPoleTab(pole.id, 'schedule-items')
                              }
                            />

                            <button
                              title="Excluir polo"
                              className="flex h-9 w-9 items-center justify-center rounded-lg border border-red-600 bg-red-600 transition hover:bg-red-700"
                              onClick={() => handleDeletePole(pole)}
                            >
                              <IoTrash size={14} color={colors.white} />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {activeTab === 'performances' && (
                <div className="mt-6">
                  {eventDetail.poles.length === 0 ? (
                    <EmptyState
                      title="Sem polos para performances"
                      description="Crie um polo para depois cadastrar performances."
                    />
                  ) : (
                    <>
                      <div className="mb-4 flex items-end justify-between gap-3 md:flex-col md:items-stretch">
                        <div className="w-full max-w-sm">
                          <h4 className="mb-2 text-sm font-bold">
                            Selecionar polo
                          </h4>
                          <select
                            value={selectedPoleId ?? ''}
                            onChange={(event) =>
                              setSelectedPoleId(event.currentTarget.value)
                            }
                            className="w-full rounded border border-gray-300 px-4 py-2 text-sm focus:border-gray-500 focus:outline-none"
                          >
                            {eventDetail.poles.map((pole) => (
                              <option key={pole.id} value={pole.id}>
                                {pole.name} ({pole.discoveryStatus})
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="flex items-center gap-3 md:w-full md:flex-col md:items-stretch">
                          <FilterToggle
                            checked={showCancelledPerformances}
                            onChange={setShowCancelledPerformances}
                            label="Exibir cancelados"
                          />
                          <Button
                            title="Adicionar performance"
                            variant="purple"
                            className="w-[190px] md:w-full"
                            disabled={!selectedPoleId}
                            onClick={() => {
                              if (!selectedPoleId) return

                              openModal({
                                children: (
                                  <FormEventPerformance
                                    eventId={selectedEventId}
                                    poleId={selectedPoleId}
                                    onSaved={async () => {
                                      await refreshCurrentContext()
                                    }}
                                  />
                                ),
                              })
                            }}
                          />
                        </div>
                      </div>

                      {selectedPole &&
                        selectedPole.discoveryStatus !== 'PUBLISHED' && (
                          <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
                            Publique o polo antes de publicar performances.
                          </div>
                        )}

                      {selectedPolePerformances.length === 0 && (
                        <EmptyState
                          title="Nenhuma performance visivel"
                          description="Voce pode adicionar performances agora ou ajustar o filtro de cancelados."
                        />
                      )}

                      <div className="space-y-3">
                        {selectedPolePerformances.map((performance) => (
                          <div
                            key={performance.id}
                            className="rounded-xl border p-4"
                          >
                            <div className="flex items-start justify-between gap-4 md:flex-col">
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 md:flex-wrap">
                                  <span
                                    className={`rounded-full border px-2 py-1 text-xs font-semibold ${resolvePublicationStatusClass(
                                      performance.status,
                                    )}`}
                                  >
                                    {performance.status}
                                  </span>
                                  {performance.isHeadliner && (
                                    <span className="rounded-full border border-purple-200 bg-purple-100 px-2 py-1 text-xs font-semibold text-purple-700">
                                      Headliner
                                    </span>
                                  )}
                                </div>
                                <p className="mt-2 font-bold text-gray-700">
                                  {performance.title ||
                                    performance.artist?.title ||
                                    'Sem titulo'}
                                </p>
                                <p className="mt-1 text-sm text-gray-500">
                                  Artista:{' '}
                                  {performance.artist?.title ||
                                    performance.artistId ||
                                    '-'}
                                </p>
                                <p className="mt-1 text-sm text-gray-500">
                                  Data:{' '}
                                  {formatDateTime(performance.performanceDate)}
                                </p>
                                <p className="mt-1 text-sm text-gray-500">
                                  Horario: {performance.startTime || '--:--'} -{' '}
                                  {performance.endTime || '--:--'}
                                </p>
                              </div>
                              <div className="flex shrink-0 flex-wrap items-center gap-2 md:w-full">
                                <Button
                                  title="Editar"
                                  variant="outline"
                                  className="w-[90px] md:flex-1"
                                  onClick={() => {
                                    if (!selectedPoleId) return

                                    openModal({
                                      children: (
                                        <FormEventPerformance
                                          eventId={selectedEventId}
                                          poleId={selectedPoleId}
                                          performance={performance}
                                          onSaved={async () => {
                                            await refreshCurrentContext()
                                          }}
                                        />
                                      ),
                                    })
                                  }}
                                />

                                {performance.status === 'PUBLISHED' ? (
                                  <Button
                                    title="Despublicar"
                                    variant="outline"
                                    className="w-[120px] md:flex-1"
                                    isLoading={
                                      runningAction ===
                                      `unpublish-performance-${performance.id}`
                                    }
                                    onClick={() =>
                                      handleUnpublishPerformance(performance)
                                    }
                                  />
                                ) : (
                                  <Button
                                    title="Publicar"
                                    variant="purple"
                                    className="w-[100px] md:flex-1"
                                    disabled={
                                      selectedPole?.discoveryStatus !==
                                        'PUBLISHED' ||
                                      performance.status === 'CANCELLED'
                                    }
                                    isLoading={
                                      runningAction ===
                                      `publish-performance-${performance.id}`
                                    }
                                    onClick={() =>
                                      handlePublishPerformance(performance)
                                    }
                                  />
                                )}

                                {performance.status !== 'CANCELLED' && (
                                  <Button
                                    title="Cancelar"
                                    variant="outline"
                                    className="w-[100px] md:flex-1"
                                    isLoading={
                                      runningAction ===
                                      `cancel-performance-${performance.id}`
                                    }
                                    onClick={() =>
                                      handleCancelPerformance(performance)
                                    }
                                  />
                                )}

                                <button
                                  title="Excluir performance"
                                  className="flex h-9 w-9 items-center justify-center rounded-lg border border-red-600 bg-red-600 transition hover:bg-red-700"
                                  onClick={() =>
                                    handleDeletePerformance(performance)
                                  }
                                >
                                  <IoTrash size={14} color={colors.white} />
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}

              {activeTab === 'schedule-items' && (
                <div className="mt-6">
                  {eventDetail.poles.length === 0 ? (
                    <EmptyState
                      title="Sem polos para agenda editorial"
                      description="Crie um polo para depois cadastrar schedule items."
                    />
                  ) : (
                    <>
                      <div className="mb-4 flex items-end justify-between gap-3 md:flex-col md:items-stretch">
                        <div className="w-full max-w-sm">
                          <h4 className="mb-2 text-sm font-bold">
                            Selecionar polo
                          </h4>
                          <select
                            value={selectedPoleId ?? ''}
                            onChange={(event) =>
                              setSelectedPoleId(event.currentTarget.value)
                            }
                            className="w-full rounded border border-gray-300 px-4 py-2 text-sm focus:border-gray-500 focus:outline-none"
                          >
                            {eventDetail.poles.map((pole) => (
                              <option key={pole.id} value={pole.id}>
                                {pole.name} ({pole.discoveryStatus})
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="flex items-center gap-3 md:w-full md:flex-col md:items-stretch">
                          <FilterToggle
                            checked={showCancelledScheduleItems}
                            onChange={setShowCancelledScheduleItems}
                            label="Exibir cancelados"
                          />
                          <Button
                            title="Adicionar schedule item"
                            variant="purple"
                            className="w-[220px] md:w-full"
                            disabled={!selectedPoleId}
                            onClick={() => {
                              if (!selectedPoleId || !eventDetail.event.slug)
                                return

                              openModal({
                                children: (
                                  <FormEventScheduleItem
                                    eventId={selectedEventId}
                                    poleId={selectedPoleId}
                                    eventSlug={eventDetail.event.slug}
                                    onSaved={async () => {
                                      await refreshCurrentContext()
                                    }}
                                  />
                                ),
                              })
                            }}
                          />
                        </div>
                      </div>

                      {selectedPoleScheduleItems.length === 0 && (
                        <EmptyState
                          title="Nenhum schedule item visivel"
                          description="Cadastre um item editorial ou ajuste o filtro de cancelados."
                        />
                      )}

                      <div className="space-y-3">
                        {selectedPoleScheduleItems.map((scheduleItem) => (
                          <div
                            key={scheduleItem.id}
                            className="rounded-xl border p-4"
                          >
                            <div className="flex items-start justify-between gap-4 md:flex-col">
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 md:flex-wrap">
                                  <span
                                    className={`rounded-full border px-2 py-1 text-xs font-semibold ${resolvePublicationStatusClass(
                                      scheduleItem.status,
                                    )}`}
                                  >
                                    {scheduleItem.status}
                                  </span>
                                  <span className="rounded-full border border-sky-200 bg-sky-50 px-2 py-1 text-xs font-semibold text-sky-700">
                                    {scheduleItemTypeLabels[scheduleItem.type]}
                                  </span>
                                </div>
                                <p className="mt-2 font-bold text-gray-700">
                                  {scheduleItem.title}
                                </p>
                                <p className="mt-1 text-sm text-gray-500">
                                  Inicio:{' '}
                                  {formatDateTime(scheduleItem.startsAt)}
                                </p>
                                <p className="mt-1 text-sm text-gray-500">
                                  Fim: {formatDateTime(scheduleItem.endsAt)}
                                </p>
                                {scheduleItem.description && (
                                  <p className="mt-1 text-sm text-gray-500">
                                    {scheduleItem.description}
                                  </p>
                                )}
                              </div>
                              <div className="flex shrink-0 flex-wrap items-center gap-2 md:w-full">
                                <Button
                                  title="Editar"
                                  variant="outline"
                                  className="w-[90px] md:flex-1"
                                  onClick={() => {
                                    if (
                                      !selectedPoleId ||
                                      !eventDetail.event.slug
                                    )
                                      return

                                    openModal({
                                      children: (
                                        <FormEventScheduleItem
                                          eventId={selectedEventId}
                                          poleId={selectedPoleId}
                                          eventSlug={eventDetail.event.slug}
                                          scheduleItem={scheduleItem}
                                          onSaved={async () => {
                                            await refreshCurrentContext()
                                          }}
                                        />
                                      ),
                                    })
                                  }}
                                />

                                {scheduleItem.status === 'PUBLISHED' ? (
                                  <Button
                                    title="Despublicar"
                                    variant="outline"
                                    className="w-[120px] md:flex-1"
                                    isLoading={
                                      runningAction ===
                                      `unpublish-schedule-item-${scheduleItem.id}`
                                    }
                                    onClick={() =>
                                      handleUnpublishScheduleItem(scheduleItem)
                                    }
                                  />
                                ) : (
                                  <Button
                                    title="Publicar"
                                    variant="purple"
                                    className="w-[100px] md:flex-1"
                                    disabled={
                                      scheduleItem.status === 'CANCELLED'
                                    }
                                    isLoading={
                                      runningAction ===
                                      `publish-schedule-item-${scheduleItem.id}`
                                    }
                                    onClick={() =>
                                      handlePublishScheduleItem(scheduleItem)
                                    }
                                  />
                                )}

                                {scheduleItem.status !== 'CANCELLED' && (
                                  <Button
                                    title="Cancelar"
                                    variant="outline"
                                    className="w-[100px] md:flex-1"
                                    isLoading={
                                      runningAction ===
                                      `cancel-schedule-item-${scheduleItem.id}`
                                    }
                                    onClick={() =>
                                      handleCancelScheduleItem(scheduleItem)
                                    }
                                  />
                                )}

                                <button
                                  title="Excluir schedule item"
                                  className="flex h-9 w-9 items-center justify-center rounded-lg border border-red-600 bg-red-600 transition hover:bg-red-700"
                                  onClick={() =>
                                    handleDeleteScheduleItem(scheduleItem)
                                  }
                                >
                                  <IoTrash size={14} color={colors.white} />
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}

              {activeTab === 'schedule' && (
                <div className="mt-6">
                  {eventDetail.poles.length === 0 ? (
                    <EmptyState
                      title="Sem polos para agenda consolidada"
                      description="Crie um polo para visualizar a timeline unificada."
                    />
                  ) : (
                    <>
                      <div className="mb-4 flex items-end justify-between gap-3 md:flex-col md:items-stretch">
                        <div className="w-full max-w-sm">
                          <h4 className="mb-2 text-sm font-bold">
                            Selecionar polo
                          </h4>
                          <select
                            value={selectedPoleId ?? ''}
                            onChange={(event) =>
                              setSelectedPoleId(event.currentTarget.value)
                            }
                            className="w-full rounded border border-gray-300 px-4 py-2 text-sm focus:border-gray-500 focus:outline-none"
                          >
                            {eventDetail.poles.map((pole) => (
                              <option key={pole.id} value={pole.id}>
                                {pole.name} ({pole.discoveryStatus})
                              </option>
                            ))}
                          </select>
                        </div>

                        <FilterToggle
                          checked={showCancelledSchedule}
                          onChange={setShowCancelledSchedule}
                          label="Exibir cancelados"
                        />
                      </div>

                      {selectedPoleSchedule.length === 0 && (
                        <EmptyState
                          title="Nenhum item visivel na agenda"
                          description="A timeline consolidada usa performances e schedule items do polo selecionado."
                        />
                      )}

                      <div className="space-y-3">
                        {selectedPoleSchedule.map((entry) => (
                          <div
                            key={`${entry.kind}-${entry.id}`}
                            className="rounded-xl border p-4"
                          >
                            <div className="flex items-start justify-between gap-4 md:flex-col">
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 md:flex-wrap">
                                  <span
                                    className={`rounded-full border px-2 py-1 text-xs font-semibold ${resolvePublicationStatusClass(
                                      entry.status,
                                    )}`}
                                  >
                                    {entry.status}
                                  </span>
                                  <span
                                    className={`rounded-full border px-2 py-1 text-xs font-semibold ${
                                      entry.kind === 'PERFORMANCE'
                                        ? 'border-purple-200 bg-purple-50 text-purple-700'
                                        : 'border-sky-200 bg-sky-50 text-sky-700'
                                    }`}
                                  >
                                    {entry.kind}
                                  </span>
                                </div>
                                <p className="mt-2 font-bold text-gray-700">
                                  {entry.title ||
                                    entry.artistName ||
                                    'Item sem titulo'}
                                </p>
                                <p className="mt-1 text-sm text-gray-500">
                                  Inicio: {formatDateTime(entry.startsAt)}
                                </p>
                                <p className="mt-1 text-sm text-gray-500">
                                  Fim: {formatDateTime(entry.endsAt)}
                                </p>
                                {entry.kind === 'PERFORMANCE' &&
                                  entry.artistName && (
                                    <p className="mt-1 text-sm text-gray-500">
                                      Artista: {entry.artistName}
                                    </p>
                                  )}
                                {entry.kind === 'SCHEDULE_ITEM' &&
                                  entry.type && (
                                    <p className="mt-1 text-sm text-gray-500">
                                      Tipo: {scheduleItemTypeLabels[entry.type]}
                                    </p>
                                  )}
                                {entry.description && (
                                  <p className="mt-1 text-sm text-gray-500">
                                    {entry.description}
                                  </p>
                                )}
                              </div>
                              <div className="text-sm text-gray-500">
                                Ordem: {entry.sortOrder ?? '-'}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </section>
      )}
    </div>
  )
}
